import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, DollarSignIcon, TrendingUpIcon, DownloadIcon, FileSpreadsheetIcon, ArchiveIcon, PowerIcon, RotateCcwIcon, BarChartIcon, LockIcon, UserIcon, TagIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Opportunity, OpportunityStage } from '../../types';
import { formatCurrency, getStageName, getStageColor } from '../../utils/helpers';
import { exportOpportunitiesToExcel } from '../../utils/excelExport';
import { exportOpportunitiesToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Opportunities() {
  const {
    opportunities,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    contacts,
    patients
  } = useData();
  const {
    user,
    users
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const {
    confirm
  } = useConfirm();
  const readOnly = isReadOnly('opportunities');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // View Mode State
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactId: '',
    responsibleUserId: '',
    opportunityType: '',
    stage: 'nuevo_lead' as OpportunityStage,
    amount: 0,
    probability: 0,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Load statuses
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-opportunities-status');
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch (e) {
      logger.error('Error loading opportunity statuses', e);
    }
  }, []);
  const updateItemStatus = (id: string, status: 'archived' | 'deactivated' | null) => {
    const newStatuses = {
      ...itemStatuses
    };
    if (status === null) {
      delete newStatuses[id];
    } else {
      newStatuses[id] = status;
    }
    setItemStatuses(newStatuses);
    localStorage.setItem('savidna-opportunities-status', JSON.stringify(newStatuses));
    const supabaseStatus = status === null ? 'active' : status;
    updateOpportunity(id, {
      status: supabaseStatus
    } as any).catch(e => logger.error('Error updating opportunity status in Supabase:', e));
  };
  const filteredOpportunities = opportunities.filter(o => {
    const status = itemStatuses[o.id];
    const matchesStatus = viewMode === 'active' ? !status : status === viewMode;
    if (!matchesStatus) return false;
    const contactName = o.contactId ? contacts.find(c => c.id === o.contactId)?.name || '' : '';
    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase()) || contactName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = !filterStage || o.stage === filterStage;
    return matchesSearch && matchesStage;
  });
  const {
    paginatedItems: paginatedOpportunities,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredOpportunities);
  const stageOptions = [{
    value: 'nuevo_lead',
    label: 'Nuevo Lead'
  }, {
    value: 'contactado',
    label: 'Contactado'
  }, {
    value: 'evaluacion_consulta',
    label: 'Evaluación / Consulta'
  }, {
    value: 'cotizacion_enviada',
    label: 'Cotización Enviada'
  }, {
    value: 'confirmado',
    label: 'Confirmado'
  }, {
    value: 'cita_agendada',
    label: 'Cita Agendada'
  }, {
    value: 'servicio_realizado',
    label: 'Servicio Realizado'
  }];
  const opportunityTypeOptions = [{
    value: 'masaje',
    label: 'Masaje'
  }, {
    value: 'facial',
    label: 'Facial'
  }, {
    value: 'sueroterapia',
    label: 'Sueroterapía'
  }, {
    value: 'ozonoterapia',
    label: 'Ozonoterapía'
  }, {
    value: 'seguimiento_post_servicio',
    label: 'Seguimiento Post-Servicio'
  }, {
    value: 'promocion',
    label: 'Promoción'
  }];
  const contactOptions = [{
    value: '',
    label: 'Seleccionar contacto...'
  }, ...contacts.map(c => ({
    value: c.id,
    label: c.name
  }))];
  const userOptions = [{
    value: '',
    label: 'Seleccionar responsable...'
  }, ...(users.length > 0 ? users.map(u => ({
    value: u.id,
    label: u.name
  })) : user ? [{
    value: user.id,
    label: user.name
  }] : [])];
  const contactSourceLabels: Record<string, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    pagina_web: 'Página Web',
    google: 'Google',
    referido: 'Referido',
    otro: 'Otro',
    // Legacy values
    web: 'Web',
    telefono: 'Teléfono',
    redes_sociales: 'Redes Sociales',
    evento: 'Evento'
  };
  const getContactSourceLabel = (contactId: string | null) => {
    if (!contactId) return 'Sin contacto';
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return 'Sin contacto';
    const source = (contact as any).contactSource || 'otro';
    return contactSourceLabels[source] || source;
  };
  const getLeadSourceLabel = (source: string) => {
    return contactSourceLabels[source] || source;
  };
  const getOpportunityTypeLabel = (type: string) => {
    return opportunityTypeOptions.find(o => o.value === type)?.label || type;
  };
  const getResponsibleName = (userId: string | null) => {
    if (!userId) return '-';
    const found = users.find(u => u.id === userId);
    if (found) return found.name;
    if (user && user.id === userId) return user.name;
    return '-';
  };
  // Auto-fill from contact when selected
  const handleContactChange = (contactId: string) => {
    setFormData({
      ...formData,
      contactId
    });
  };
  const openNewForm = () => {
    setSelectedOpportunity(null);
    setFormData({
      name: '',
      contactId: '',
      responsibleUserId: user?.id || '',
      opportunityType: 'masaje',
      stage: 'nuevo_lead',
      amount: 0,
      probability: 0,
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setFormData({
      name: opportunity.name,
      contactId: opportunity.contactId || '',
      responsibleUserId: (opportunity as any).responsibleUserId || '',
      opportunityType: (opportunity as any).opportunityType || 'masaje',
      stage: opportunity.stage,
      amount: opportunity.amount,
      probability: opportunity.probability,
      notes: opportunity.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Action Handlers
  const openActionConfirm = (opportunity: Opportunity, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedOpportunity(opportunity);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Oportunidad';
      message = `¿Estás seguro de que deseas archivar la oportunidad "${opportunity.name}"? Podrás restaurarla más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Oportunidad';
      message = `¿Estás seguro de que deseas desactivar la oportunidad "${opportunity.name}"? No aparecerá en las listas activas.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Oportunidad';
      message = `¿Estás seguro de que deseas eliminar permanentemente la oportunidad "${opportunity.name}"? Esta acción no se puede deshacer.`;
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          updateItemStatus(opportunity.id, 'archived');
          showToast({
            type: 'info',
            title: 'Oportunidad archivada',
            message: `${opportunity.name} fue archivada.`
          });
        } else if (type === 'deactivate') {
          updateItemStatus(opportunity.id, 'deactivated');
          showToast({
            type: 'warning',
            title: 'Oportunidad desactivada',
            message: `${opportunity.name} fue desactivada.`
          });
        } else if (type === 'delete') {
          const name = opportunity.name;
          deleteOpportunity(opportunity.id);
          const newStatuses = {
            ...itemStatuses
          };
          delete newStatuses[opportunity.id];
          setItemStatuses(newStatuses);
          localStorage.setItem('savidna-opportunities-status', JSON.stringify(newStatuses));
          showToast({
            type: 'error',
            title: 'Oportunidad eliminada',
            message: `${name} fue eliminada permanentemente.`
          });
        }
        setSelectedOpportunity(null);
      }
    });
  };
  const handleRestore = (opportunity: Opportunity) => {
    updateItemStatus(opportunity.id, null);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.amount < 0) errors.amount = 'El monto no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: selectedOpportunity ? 'Guardar Cambios' : 'Crear Oportunidad',
      message: selectedOpportunity ? '¿Estás seguro de que deseas guardar los cambios de esta oportunidad?' : '¿Estás seguro de que deseas registrar esta nueva oportunidad?',
      confirmText: 'Guardar',
      onConfirm: () => {
        // Get contactSource from the selected contact
        const selectedContact = formData.contactId ? contacts.find(c => c.id === formData.contactId) : null;
        const contactSource = selectedContact ? selectedContact.contactSource || 'otro' : '';
        const opportunityData = {
          ...formData,
          contactId: formData.contactId || null,
          contactSource,
          responsibleUserId: formData.responsibleUserId || null
        };
        if (selectedOpportunity) {
          updateOpportunity(selectedOpportunity.id, opportunityData);
          showToast({
            type: 'success',
            title: 'Oportunidad actualizada',
            message: `${formData.name} fue actualizada correctamente.`
          });
        } else {
          addOpportunity(opportunityData);
          showToast({
            type: 'success',
            title: 'Oportunidad creada',
            message: `${formData.name} fue registrada correctamente.`
          });
        }
        setIsFormOpen(false);
      }
    });
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = opportunities;
    if (dateFrom && dateTo) {
      dataToExport = opportunities.filter(o => {
        const dateStr = new Date(o.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportOpportunitiesToExcel(dataToExport);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = opportunities;
    if (dateFrom && dateTo) {
      dataToExport = opportunities.filter(o => {
        const dateStr = new Date(o.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportOpportunitiesToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-4136" data-mp="11qzjtej8jcps">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 shadow-lg text-white" data-id="element-4137" data-mp="wurbajzjmhrl">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-4138" data-mp="1piww16qlk6rf" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-4139" data-mp="1bhu9ytzlqwgi" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-4140" data-mp="3qh5r4r3ed1" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-4141" data-mp="6364o8xeozl">
          <div data-id="element-4142" data-mp="2fse332k1a3pa">
            <div className="flex items-center gap-2 mb-1" data-id="element-4143" data-mp="2frw07n2v8r8e">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-4144" data-mp="2fqb43t0wl08f">
                <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-4145" data-mp="2kpxgy1ctkegp" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-4146" data-mp="2pkjuob6dij3y">Oportunidades</h1>
            </div>
            <p className="text-white/80 text-sm font-medium" data-id="element-4147" data-mp="2uh1tyvixpp8w">
              Pipeline de ventas y seguimiento de negocios
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-4148" data-mp="33hqb3z3mk5q0">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-4149" data-mp="194gwxu04vw1f">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4151" data-mp="1e3tub8omsli9" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4150" data-mp="196lcj97eajhj">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4153" data-mp="2le9ibgc43s5r" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4152" data-mp="2qcjf1vdhj0th">
                Exportar Excel
              </Button>}
            {canCreate('opportunities') && <Button variant="outline" onClick={openNewForm} className="text-white border-transparent shadow-lg" style={{
            backgroundColor: '#1aa2b2'
          }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#178fa0'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1aa2b2'} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4155" data-mp="3nslxt5237vzn" />} data-id="element-4154" data-mp="353scurrjjshp">
                Nueva Oportunidad
              </Button>}
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-4156" data-mp="3st9yyjk15t7l" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-4157" data-mp="3jq8usoeq6c6k" />
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-4158" data-mp="34zi27e5b792e">
          <LockIcon className="w-4 h-4" data-id="element-4159" data-mp="2w1px12taxhsq" />
          <span data-id="element-4160" data-mp="3029s80ubebc8">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            oportunidades.
          </span>
        </div>}

      {/* View Tabs & Filters */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-4161" data-mp="3bj7dmhsj4yu8">
        <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl" data-id="element-4162" data-mp="3bh39byqpxxtf">
          <button onClick={() => setViewMode('active')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'active' ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4163" data-mp="36i07so9y69q7">
            Activas
          </button>
          <button onClick={() => setViewMode('archived')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'archived' ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4164" data-mp="2ntv6dngrj0uk">
            Archivadas
          </button>
          <button onClick={() => setViewMode('deactivated')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'deactivated' ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4165" data-mp="parthccsijqt">
            Desactivadas
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" data-id="element-4166" data-mp="5p57axihol7f">
          <div className="w-full sm:w-48" data-id="element-4167" data-mp="anov73gn872f">
            <Select options={[{
            value: '',
            label: 'Todas las etapas'
          }, ...stageOptions]} value={filterStage} onChange={setFilterStage} placeholder="Filtrar por etapa" data-id="element-4168" data-mp="akkkdn8oad2s" />
          </div>
          <div className="relative flex-1 md:w-64" data-id="element-4169" data-mp="3o0i5ouujuuu8">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4170" data-mp="3nz74n2sho16t" />
            <Input style={{
            paddingLeft: '2.5rem'
          }} placeholder="Buscar oportunidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-4171" data-mp="30xxgslv3x033" />
          </div>
        </div>
      </div>

      {/* Opportunities Table */}
      <Card padding="none" className="overflow-hidden" data-id="element-4172" data-mp="2i8r5hjwn7arb">
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4173" data-mp="2n6iv279teksc">
          {paginatedOpportunities.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-4174" data-mp="2rytvrccdqaly">
              <p className="text-sm" data-id="element-4175" data-mp="1uhbxpbm3k18o">No se encontraron oportunidades</p>
            </div> : paginatedOpportunities.map(opp => {
          const contactName = opp.contactId ? contacts.find(c => c.id === opp.contactId)?.name || 'Sin asignar' : 'Sin contacto';
          return <div key={opp.id} className="px-4 py-3 flex items-start gap-3" data-id="element-4176" data-mp="7kvzc1qfwskq">
                  <div className="flex-1 min-w-0" data-id="element-4177" data-mp="2mazi3f3oawt">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4178" data-mp="2lj2co5uu54f">
                      {opp.name || opp.title || 'Oportunidad'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4179" data-mp="3p0ucpwsc9k1p">
                      {contactName}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-4180" data-mp="3ky7cp8likstf">
                      {opp.stage && <Badge variant="info" className="text-xs py-0 px-1.5" data-id="element-4181" data-mp="3b24bq62ef082">
                          {opp.stage}
                        </Badge>}
                      {opp.amount && <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-4182" data-mp="2wevzr319mqv1">
                          {typeof opp.amount === 'number' ? `$${opp.amount.toLocaleString()}` : opp.amount}
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4183" data-mp="2ri5i4b9tbn7i">
                    {viewMode === 'active' && canEdit('opportunities') && <button onClick={() => openEditForm(opp)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-4184" data-mp="3k3ygm8o2sfoh">
                        <EditIcon className="w-4 h-4" data-id="element-4185" data-mp="1zekhf5s7hpy" />
                      </button>}
                    {viewMode === 'active' && canDelete('opportunities') && <button onClick={() => openActionConfirm(opp, 'archive')} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-4186" data-mp="6ry34qjbt31m">
                        <ArchiveIcon className="w-4 h-4" data-id="element-4187" data-mp="215ab1dv6pt5d" />
                      </button>}
                    {viewMode !== 'active' && <button onClick={() => restoreItem(opp.id)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" data-id="element-4188" data-mp="18ho1zq36twb4">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-4189" data-mp="1igvfcd9x9qy8" />
                      </button>}
                  </div>
                </div>;
        })}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-4190" data-mp="2twtw73bcdha0">
          <table className="w-full" data-id="element-4191" data-mp="2ov4ith0ncrir">
            <thead data-id="element-4192" data-mp="2oy8v71o9mtki">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-4193" data-mp="yf29bpl61zkw">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4194" data-mp="13ddl45v0uk5r">
                  Oportunidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4195" data-mp="1cebr2tp0t1qz">
                  Contacto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4196" data-mp="1hbu16nerxn0k">
                  Origen
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4197" data-mp="1r49j5kq2mblg">
                  Tipo
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4198" data-mp="205h5d4fbblz6">
                  Etapa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4199" data-mp="2f7fw3tqddsdm">
                  Monto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4200" data-mp="25900bdgeduxs">
                  Probabilidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4201" data-mp="219rc0o44mp9m">
                  Responsable
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4202" data-mp="2t0no3mid4pvu">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4203" data-mp="3go582vvdyjo9">
              {paginatedOpportunities.length === 0 ? <tr data-id="element-4204" data-mp="3bx6n3izgcxeo">
                  <td colSpan={9} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-4205" data-mp="3bvvmfs1b05rs">
                    No se encontraron oportunidades{' '}
                    {viewMode === 'active' ? '' : viewMode === 'archived' ? 'archivadas' : 'desactivadas'}
                  </td>
                </tr> : paginatedOpportunities.map(opp => {
              const contactName = opp.contactId ? contacts.find(c => c.id === opp.contactId)?.name || 'Sin asignar' : 'Sin asignar';
              const oppType = (opp as any).opportunityType || '';
              const oppResponsible = (opp as any).responsibleUserId || null;
              // Use saved contactSource, fallback to live contact data
              const sourceValue = opp.contactSource || (opp.contactId ? contacts.find(c => c.id === opp.contactId)?.contactSource || '' : '');
              const contactSourceDisplay = sourceValue ? contactSourceLabels[sourceValue] || sourceValue : 'Sin origen';
              return <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-4206" data-mp="9yroxwjgy61t">
                      <td className="py-4 px-6" data-id="element-4207" data-mp="jp48blfjrt6d">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-4208" data-mp="xpnaibxc050m">
                          {opp.name}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-4209" data-mp="srdj8ceq9oka">
                        <p className="text-sm text-gray-900 dark:text-gray-100" data-id="element-4210" data-mp="2zg8j1nxvob5c">
                          {contactName}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-4211" data-mp="28orkwrlwxoie">
                        <Badge variant="info" data-id="element-4212" data-mp="28o01sg9i5j90">{contactSourceDisplay}</Badge>
                      </td>
                      <td className="py-4 px-6" data-id="element-4213" data-mp="2dm9xcrz52lv5">
                        {oppType ? <Badge variant="default" data-id="element-4214" data-mp="2ifw09sfi3bdr">
                            {getOpportunityTypeLabel(oppType)}
                          </Badge> : <span className="text-xs text-gray-400 italic" data-id="element-4215" data-mp="1pqp2wwrs8ru3">
                            Sin tipo
                          </span>}
                      </td>
                      <td className="py-4 px-6" data-id="element-4216" data-mp="1znjzha4hwi5q">
                        <div className="flex items-center gap-2" data-id="element-4217" data-mp="1zms3vzawekfg">
                          <div className={`w-2.5 h-2.5 rounded-full ${getStageColor(opp.stage)}`} data-id="element-4218" data-mp="24o7ndmxfgivq" />

                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-4219" data-mp="3g2bhct40m70s">
                            {getStageName(opp.stage)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900 dark:text-gray-100" data-id="element-4220" data-mp="zfmzr6wdv3w9">
                        {formatCurrency(opp.amount)}
                      </td>
                      <td className="py-4 px-6" data-id="element-4221" data-mp="pq3exvp40zyr">
                        <div className="flex items-center gap-3" data-id="element-4222" data-mp="ppaipons1suv">
                          <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4223" data-mp="kriga0piaocf">
                            <div className="h-full bg-emerald-500 rounded-full" style={{
                        width: `${opp.probability}%`
                      }} data-id="element-4224" data-mp="gp5ji1sqm0rs" />
                          </div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400" data-id="element-4225" data-mp="263adymbogubs">
                            {opp.probability}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-4226" data-mp="2rdbkjm29omgt">
                        <div className="flex items-center gap-2" data-id="element-4227" data-mp="2mgkzslfip2p2">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center" data-id="element-4228" data-mp="2me8lci8yxagr">
                            <UserIcon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" data-id="element-4229" data-mp="2w5miwkhd0079" />
                          </div>
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-4230" data-mp="28gtqgxzy4ahe">
                            {getResponsibleName(oppResponsible)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-4231" data-mp="2dfemel860801">
                        <div className="flex items-center justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-4232" data-mp="2ddtnykiqc4ww">
                          {viewMode === 'active' ? <>
                              {canEdit('opportunities') && <button onClick={() => openEditForm(opp)} className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Editar" data-id="element-4233" data-mp="73tafdbaeom">
                                  <EditIcon className="w-4 h-4" data-id="element-4234" data-mp="iymucgnrzio0" />
                                </button>}
                              {canDelete('opportunities') && <>
                                  <button onClick={() => {
                          setSelectedOpportunity(opp);
                          setIsArchiveConfirmOpen(true);
                        }} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-4235" data-mp="98tvmg8qey16">
                                    <ArchiveIcon className="w-4 h-4" data-id="element-4236" data-mp="1bjjw95xu1pcy" />
                                  </button>
                                  <button onClick={() => {
                          setSelectedOpportunity(opp);
                          setIsDeactivateConfirmOpen(true);
                        }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-4237" data-mp="17lvzryghroiy">
                                    <PowerIcon className="w-4 h-4" data-id="element-4238" data-mp="ev473aymxi0d" />
                                  </button>
                                </>}
                            </> : canDelete('opportunities') && <button onClick={() => handleRestore(opp)} className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-4239" data-mp="2qih57gein1jm">
                                <RotateCcwIcon className="w-4 h-4" data-id="element-4240" data-mp="22u7m4m8osrw6" />
                              </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="px-6 pb-4" data-id="element-4241" data-mp="2gmnv03o9rk50">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-4242" data-mp="1k36s8cmfbmjh" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedOpportunity ? 'Editar Oportunidad' : 'Nueva Oportunidad'} size="lg" icon={<TrendingUpIcon className="w-5 h-5" data-id="element-4244" data-mp="19xf539gaifjt" />} accentColor="emerald" data-id="element-4243" data-mp="zyfyrxizyvzz">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4245" data-mp="1nok1m2od2rpx">
          {/* Basic Info */}
          <div data-id="element-4246" data-mp="1iq8qzpq8sd6n">
            <div className="flex items-center gap-2 mb-4" data-id="element-4247" data-mp="1iqsavbzgdbnb">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-4248" data-mp="1xokp80t9jtzx"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4249" data-mp="1sphh27hzl7nq">
                Información General
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4250" data-mp="3c4k53c7elwn">
              <Input label="Nombre de la Oportunidad" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Paquete de tratamientos faciales" data-id="element-4251" data-mp="29y0lnpgmv554" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4252" data-mp="1r6zlwz7ov5ki">
                <Select label="Contacto" options={contactOptions} value={formData.contactId} onChange={handleContactChange} searchable data-id="element-4253" data-mp="1v9e5jlj6m9cu" />

                <Select label="Usuario Responsable" options={userOptions} value={formData.responsibleUserId} onChange={value => setFormData({
                ...formData,
                responsibleUserId: value
              })} data-id="element-4254" data-mp="1m9r2ywepuxi7" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4255" data-mp="3a5xkzrku2he2">
                <div data-id="element-4256" data-mp="35746pwxq2mcu">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-4257" data-mp="358f6zmrylbz7">
                    Origen del Contacto
                  </label>
                  <div className="px-3 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 cursor-not-allowed" data-id="element-4258" data-mp="1rghuy9nuxdv">
                    {formData.contactId ? getContactSourceLabel(formData.contactId) : 'Selecciona un contacto primero'}
                  </div>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-4259" data-mp="kherhit3wyjm">
                    Se obtiene automáticamente del contacto
                  </p>
                </div>
                <Select label="Tipo de Oportunidad" options={opportunityTypeOptions} value={formData.opportunityType} onChange={value => setFormData({
                ...formData,
                opportunityType: value
              })} data-id="element-4260" data-mp="fpdic5r8jnxt" />
              </div>
            </div>
          </div>

          {/* Deal Info */}
          <div data-id="element-4261" data-mp="16yzu18ps1nv3">
            <div className="flex items-center gap-2 mb-4" data-id="element-4262" data-mp="16xf2zt2o8n1z">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-4263" data-mp="1azjuom9k79lz"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4264" data-mp="1axfep5ydyk5c">
                Datos del Negocio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4265" data-mp="1kotllyso2m8i">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4266" data-mp="1unsloszd99jw">
                <Select label="Etapa" options={stageOptions} value={formData.stage} onChange={value => setFormData({
                ...formData,
                stage: value as OpportunityStage
              })} data-id="element-4267" data-mp="1uo0xsino0au1" />

                <Input type="number" label="Monto Estimado (DOP)" value={formData.amount} onChange={e => setFormData({
                ...formData,
                amount: parseFloat(e.target.value) || 0
              })} error={formErrors.amount} min={0} data-id="element-4268" data-mp="2k1dzv01tq31k" />
              </div>
              <div data-id="element-4269" data-mp="1dk8z8a72shtn">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-4270" data-mp="1djqt8lum2p8j">
                  Probabilidad de Cierre
                </label>
                <div className="flex items-center gap-4" data-id="element-4271" data-mp="18jbje49jfzvb">
                  <input type="range" min="0" max="100" step="5" value={formData.probability} onChange={e => setFormData({
                  ...formData,
                  probability: parseInt(e.target.value)
                })} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-emerald-500" data-id="element-4272" data-mp="3k7puijpzwfyx" />

                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-lg min-w-[60px] text-center" data-id="element-4273" data-mp="3tyu8ph7ujwsp">
                    {formData.probability}%
                  </span>
                </div>
              </div>
              <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} rows={3} placeholder="Notas adicionales sobre la oportunidad..." data-id="element-4274" data-mp="2ni8og3n3orwp" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-4275" data-mp="20wp13ee3hh57">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-4276" data-mp="2omt3jifjraum">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4277" data-mp="2fjs0xrpw41vp">
              {selectedOpportunity ? 'Guardar Cambios' : 'Crear Oportunidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Oportunidades" isExporting={isExporting} data-id="element-4278" data-mp="144v4hwrdmaeb" />

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Oportunidades a PDF" isExporting={isPdfExporting} data-id="element-4279" data-mp="10pjy4pbbg6nl" />
    </div>;
}